import OneSignal, {
  SubscriptionChangeEvent,
  UserChangeEvent,
} from "react-onesignal";

import { Env } from "../env";
import { useCallback, useEffect, useState } from "react";

// type UserAliases = {
//   email?: string;
//   name?: string;
// };

type OneSignalUser = {
  externalId: string | null;
  onesignalId: string | null;
  pushSubscriptionId: string | null;
  pushSubscriptionToken: string | null;
  pushPermission: boolean | null;
};

const user: OneSignalUser = {
  externalId: null,
  onesignalId: null,
  pushSubscriptionId: null,
  pushSubscriptionToken: null,
  pushPermission: null,
};

function handleChangeUser({ current: ev }: UserChangeEvent) {
  user.externalId = ev.externalId ?? null;
  user.onesignalId = ev.onesignalId ?? null;
}

function handleChangeUserPushSubscription({
  current: ev,
}: SubscriptionChangeEvent) {
  user.pushSubscriptionId = ev.id ?? null;
  user.pushSubscriptionToken = ev.token ?? null;
}

function handleChangeNotificationsPermission(value: boolean) {
  user.pushPermission = value;
}

type InitOptions = {
  onChangeUser?: (user: UserChangeEvent) => void;
};

async function subscribe(options?: InitOptions) {
  OneSignal.User.addEventListener("change", handleChangeUser);
  OneSignal.Notifications.addEventListener(
    "permissionChange",
    handleChangeNotificationsPermission
  );

  OneSignal.User.PushSubscription.addEventListener(
    "change",
    handleChangeUserPushSubscription
  );

  if (options?.onChangeUser) {
    OneSignal.User.addEventListener("change", options.onChangeUser);
  }
}

async function unsubscribe(options?: InitOptions) {
  OneSignal.User.removeEventListener("change", handleChangeUser);
  OneSignal.Notifications.removeEventListener(
    "permissionChange",
    handleChangeNotificationsPermission
  );
  OneSignal.User.PushSubscription.removeEventListener(
    "change",
    handleChangeUserPushSubscription
  );

  if (options?.onChangeUser) {
    OneSignal.User.removeEventListener("change", options.onChangeUser);
  }
}

if (Env.debug) {
  OneSignal.Debug.setLogLevel("trace");
}

export const Notifications = {
  options: null as InitOptions | null,

  user,

  async init(options?: InitOptions) {
    this.options = options ?? null;

    await OneSignal.init({
      appId: Env.oneSignalAppId,
      safari_web_id: Env.oneSignalSafariWebId,
      allowLocalhostAsSecureOrigin: Env.debug,
    });

    user.externalId = OneSignal.User.externalId ?? null;
    user.onesignalId = OneSignal.User.onesignalId ?? null;
    user.pushSubscriptionId = OneSignal.User.PushSubscription.id ?? null;
    user.pushSubscriptionToken = OneSignal.User.PushSubscription.token ?? null;
    user.pushPermission = OneSignal.Notifications.permission;

    await subscribe(options);

    return user;
  },

  async login(externalId: string) {
    await OneSignal.login(externalId);

    // TODO ?
    // if (aliases?.email) {
    //   await OneSignal.User.addAlias("email", aliases.email);
    // }

    // if (aliases?.name) {
    //   await OneSignal.User.addAlias("name", aliases.name);
    // }
  },

  async logout() {
    await unsubscribe(this.options ?? undefined);
    await OneSignal.logout();
  },

  async isPushSupported() {
    return OneSignal.Notifications.isPushSupported();
  },

  hasPushPermission() {
    return OneSignal.Notifications.permission;
  },

  async requestPushPermission() {
    return OneSignal.Notifications.requestPermission();
  },
};

export function useNotifications() {
  // State
  const [isRequesting, setRequesting] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    OneSignal.Notifications.permission
  );

  // Listen for permission changes
  useEffect(() => {
    const handler = (value: boolean) => setPushPermission(value);
    OneSignal.Notifications.addEventListener("permissionChange", handler);
    return () => {
      OneSignal.Notifications.removeEventListener("permissionChange", handler);
    };
  }, []);

  // Handlers
  const requestPushPermission = useCallback(async () => {
    try {
      setRequesting(true);

      const granted = await Notifications.requestPushPermission();
      setPushPermission(granted);
      return granted;
    } catch {
      setPushPermission(false);
      return false;
    } finally {
      setRequesting(false);
    }
  }, []);

  return [
    pushPermission,
    requestPushPermission,
    isRequesting,
    setPushPermission,
  ] as const;
}
